import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import RoleBasedHeader from '../../components/ui/RoleBasedHeader';
import QuickActionToolbar from '../../components/ui/QuickActionToolbar';
import TechnicianCard from './components/TechnicianCard';
import ServiceCard from './components/ServiceCard';
import CalendarView from './components/CalendarView';
import PerformanceMetrics from './components/PerformanceMetrics';
import FilterPanel from './components/FilterPanel';
import CreateOrderModal from './components/CreateOrderModal';
import Button from '../../components/ui/Button';
import { trackingService } from '../../services/trackingService';
import Icon from '../../components/AppIcon';



const DispatchControlCenter = () => {
  const navigate = useNavigate();
  const [activeView, setActiveView] = useState('calendar');
  const [isCreateOrderModalOpen, setIsCreateOrderModalOpen] = useState(false);
  const [selectedDate, setSelectedDate] = useState(new Date());
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState('');
  
  // Real-time state from Supabase
  const [technicians, setTechnicians] = useState([]);
  const [services, setServices] = useState([]);
  
  const [filters, setFilters] = useState({
    urgency: 'all',
    status: 'all',
    technician: 'all',
    serviceType: 'all',
    area: '',
    search: ''
  });

  const mockMetrics = {
    etaAccuracy: { value: 92, trend: 3 },
    appointmentAdherence: { value: 96, trend: 1 },
    noShowRate: { value: 4, trend: -1 },
    avgInterventionTime: { value: 87, trend: -5 }
  };

  const serviceTypeOptions = [
  { value: 'leak', label: 'Fuite d\'eau' },
  { value: 'installation', label: 'Installation' },
  { value: 'unclog', label: 'Débouchage' },
  { value: 'repair', label: 'Réparation' },
  { value: 'maintenance', label: 'Maintenance' },
  { value: 'heating', label: 'Chauffage' },
  { value: 'emergency', label: 'Urgence' }];


  // Load initial data
  useEffect(() => {
    const loadData = async () => {
      try {
        setIsLoading(true);
        const [techData, serviceData] = await Promise.all([
          trackingService?.getAllTechnicians(),
          trackingService?.getAllActiveAppointments()
        ]);

        setTechnicians(techData?.map((tech) => ({
          id: tech?.id,
          name: tech?.name,
          avatar: tech?.avatarUrl,
          avatarAlt: `Professional headshot of ${tech?.name}`,
          specialty: tech?.specialty,
          status: tech?.status,
          currentLocation: tech?.currentLocation ? 
            `${tech?.currentLocation?.latitude?.toFixed(4)}, ${tech?.currentLocation?.longitude?.toFixed(4)}` : 
            'Unknown',
          todayServices: serviceData?.filter((s) => s?.technicianId === tech?.id)?.length,
          maxServices: tech?.maxDailyServices,
          rating: tech?.rating,
          reviewCount: tech?.reviewCount
        })));

        setServices(serviceData || []);
      } catch (err) {
        setError(err?.message || 'Failed to load dispatch data');
        console.error('Error loading dispatch data:', err);
      } finally {
        setIsLoading(false);
      }
    };

    loadData();
  }, []);

  // Subscribe to real-time technician location updates
  useEffect(() => {
    const unsubscribe = trackingService?.subscribeToAllTechnicianLocations(
      (locationUpdate) => {
        setTechnicians((prev) =>
          prev?.map((tech) =>
            tech?.id === locationUpdate?.technicianId
              ? {
                  ...tech,
                  status: locationUpdate?.status,
                  currentLocation: `${locationUpdate?.latitude?.toFixed(4)}, ${locationUpdate?.longitude?.toFixed(4)}`
                }
              : tech
          )
        );
      }
    );

    return () => {
      unsubscribe();
    };
  }, []);

  const handleFilterChange = (field, value) => {
    setFilters((prev) => ({ ...prev, [field]: value }));
  };

  const handleResetFilters = () => {
    setFilters({
      urgency: 'all',
      status: 'all',
      technician: 'all',
      serviceType: 'all',
      area: '',
      search: ''
    });
  };

  const handleAssignService = (technicianId) => {
    console.log('Assigning service to technician:', technicianId);
  };

  const handleCallTechnician = (technicianId) => {
    console.log('Calling technician:', technicianId);
  };

  const handleContactCustomer = (serviceId) => {
    console.log('Contacting customer for service:', serviceId);
  };

  const handleReassignService = (serviceId) => {
    console.log('Reassigning service:', serviceId);
  };

  const handleViewServiceDetails = (serviceId) => {
    console.log('Viewing service details:', serviceId);
  };

  const handleAppointmentClick = (appointment) => {
    console.log('Appointment clicked:', appointment);
  };

  const handleCreateOrder = (orderData) => {
    console.log('Creating new order:', orderData);
  };

  const handleLogout = () => {
    navigate('/login-authentication');
  };

  const filteredServices = services?.filter((service) => {
    if (filters?.urgency !== 'all' && service?.urgency !== filters?.urgency) return false;
    if (filters?.status !== 'all' && service?.status !== filters?.status) return false;
    if (filters?.technician !== 'all' && service?.technicianId !== filters?.technician) return false;
    if (filters?.search && !service?.customerName?.toLowerCase()?.includes(filters?.search?.toLowerCase())) return false;
    return true;
  });

  const filteredTechnicians = technicians?.filter((tech) => {
    if (filters?.technician !== 'all' && tech?.id !== filters?.technician) return false;
    return true;
  });

  if (isLoading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="flex flex-col items-center gap-4">
          <div className="w-16 h-16 border-4 border-primary border-t-transparent rounded-full animate-spin" />
          <p className="text-sm text-muted-foreground">Chargement des données...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="max-w-md p-6 bg-card rounded-lg border border-border">
          <div className="flex items-center gap-3 mb-4">
            <Icon name="AlertCircle" size={32} className="text-error" />
            <h2 className="text-xl font-semibold text-foreground">Erreur de chargement</h2>
          </div>
          <p className="text-muted-foreground mb-4">{error}</p>
          <Button onClick={() => window.location?.reload()} variant="outline">
            Réessayer
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <RoleBasedHeader
        userRole="dispatcher"
        userName="Dispatcher Admin"
        onLogout={handleLogout} />

      <main className="main-content">
        <div className="container mx-auto px-4 py-6 max-w-[1600px]">
          <div className="flex items-center justify-between mb-6">
            <div>
              <h1 className="text-3xl font-bold text-foreground mb-2">Centre de Dispatch</h1>
              <p className="text-muted-foreground">
                Gérez les interventions et coordonnez les techniciens en temps réel
              </p>
            </div>
            <Button
              variant="default"
              size="lg"
              iconName="Plus"
              onClick={() => setIsCreateOrderModalOpen(true)}>

              Nouvelle commande
            </Button>
          </div>

          <PerformanceMetrics metrics={mockMetrics} />

          <div className="mt-6">
            <FilterPanel
              filters={filters}
              onFilterChange={handleFilterChange}
              onReset={handleResetFilters}
              technicians={technicians}
              serviceTypes={serviceTypeOptions} />

          </div>

          <div className="mt-6 flex gap-2 mb-4">
            <Button
              variant={activeView === 'calendar' ? 'default' : 'outline'}
              iconName="Calendar"
              onClick={() => setActiveView('calendar')}
            >
              Vue calendrier
            </Button>
            <Button
              variant={activeView === 'list' ? 'default' : 'outline'}
              iconName="List"
              onClick={() => setActiveView('list')}
            >
              Vue liste
            </Button>
            <Button
              variant="outline"
              iconName="Package"
              onClick={() => navigate('/inventory-management')}
            >
              Gestion stock
            </Button>
          </div>

          {activeView === 'calendar' &&
          <CalendarView
            appointments={filteredServices}
            technicians={filteredTechnicians}
            onAppointmentClick={handleAppointmentClick}
            onDateChange={setSelectedDate}
            selectedDate={selectedDate} />

          }

          {activeView === 'list' &&
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
              <div className="lg:col-span-2 space-y-4">
                <div className="bg-card rounded-lg border border-border p-4">
                  <h2 className="text-xl font-semibold text-foreground mb-4">
                    Interventions actives ({filteredServices?.length})
                  </h2>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {filteredServices?.map((service) =>
                  <ServiceCard
                    key={service?.id}
                    service={service}
                    onViewDetails={handleViewServiceDetails}
                    onReassign={handleReassignService}
                    onContactCustomer={handleContactCustomer}
                    onUpdateStatus={(serviceId, status) => console.log('Update status:', serviceId, status)} />

                  )}
                  </div>
                </div>
              </div>

              <div className="space-y-4">
                <div className="bg-card rounded-lg border border-border p-4">
                  <h2 className="text-xl font-semibold text-foreground mb-4">
                    Techniciens disponibles ({filteredTechnicians?.filter((t) => t?.status === 'available')?.length})
                  </h2>
                  <div className="space-y-3">
                    {filteredTechnicians?.map((technician) =>
                  <TechnicianCard
                    key={technician?.id}
                    technician={technician}
                    onAssignService={handleAssignService}
                    onCallTechnician={handleCallTechnician}
                    onViewDetails={(technicianId) => console.log('View technician details:', technicianId)} />

                  )}
                  </div>
                </div>
              </div>
            </div>
          }

          {activeView === 'map' &&
          <div className="bg-card rounded-lg border border-border p-4">
              <div className="aspect-video rounded-lg overflow-hidden">
                <iframe
                width="100%"
                height="100%"
                loading="lazy"
                title="Carte des interventions en temps réel"
                referrerPolicy="no-referrer-when-downgrade"
                src="https://www.google.com/maps?q=48.8566,2.3522&z=12&output=embed"
                className="w-full h-full" />

              </div>
              <div className="mt-4 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-3">
                <div className="flex items-center gap-2 p-3 bg-error/10 rounded-lg">
                  <div className="w-3 h-3 rounded-full bg-error" />
                  <span className="text-sm text-foreground">Urgent</span>
                </div>
                <div className="flex items-center gap-2 p-3 bg-warning/10 rounded-lg">
                  <div className="w-3 h-3 rounded-full bg-warning" />
                  <span className="text-sm text-foreground">Aujourd'hui</span>
                </div>
                <div className="flex items-center gap-2 p-3 bg-primary/10 rounded-lg">
                  <div className="w-3 h-3 rounded-full bg-primary" />
                  <span className="text-sm text-foreground">Planifié</span>
                </div>
                <div className="flex items-center gap-2 p-3 bg-success/10 rounded-lg">
                  <div className="w-3 h-3 rounded-full bg-success" />
                  <span className="text-sm text-foreground">Terminé</span>
                </div>
              </div>
            </div>
          }
        </div>
      </main>
      <QuickActionToolbar
        userRole="dispatcher"
        onEmergencyCall={() => console.log('Emergency call')}
        onQuickBooking={() => setIsCreateOrderModalOpen(true)}
        onSupportChat={() => console.log('Support chat')} />

      <CreateOrderModal
        isOpen={isCreateOrderModalOpen}
        onClose={() => setIsCreateOrderModalOpen(false)}
        onSubmit={handleCreateOrder}
        technicians={technicians}
        serviceTypes={serviceTypeOptions} />

    </div>);

};

export default DispatchControlCenter;